// Project-domain HTTP service (Layer 2 — transport). Wraps lib/http.ts's
// request() for the Project endpoints used by ProjectDetail.tsx,
// ProjectPhasesCard.tsx, and PhasePicker.tsx. Methods moved verbatim off
// ApiClient (apps/frontend/src/utils/api.ts), which now delegates here to
// preserve every existing `apiClient.*` call site.
import { request } from '@/lib/http'
import type { Project, ProjectMember, ProjectStatusReport, ProjectPhase, TaskDependency } from '@/types'

export const projectsApi = {
  list(params?: { status?: 'active' | 'archived' }): Promise<{ items: Project[]; total: number }> {
    const qs = params?.status ? `?status=${params.status}` : ''
    return request(`/work/projects${qs}`)
  },

  /** `id` is the project's `public_id` (ADR 0007) — the URL/route-param value. */
  get(id: string): Promise<Project> {
    return request(`/work/projects/${id}`)
  },

  create(data: {
    name: string
    description?: string
    intake_answers?: Record<string, string>
    start_date?: string
    due_date?: string
    team_id?: number
    team_ids?: number[]
    member_ids?: number[]
  }): Promise<Project> {
    return request('/work/projects', { method: 'POST', body: JSON.stringify(data) })
  },

  update(id: string, data: Partial<Pick<Project, 'name' | 'description' | 'intake_answers' | 'status' | 'start_date' | 'due_date' | 'is_template'> & { member_ids?: number[] }>): Promise<Project> {
    return request(`/work/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  },

  archive(id: string): Promise<Project> {
    return request(`/work/projects/${id}`, { method: 'DELETE' })
  },

  /** Permanent, irreversible delete — distinct from archive(). Erases the
   * project and everything under it (tasks, phases, comments, attachments). */
  deletePermanent(id: string): Promise<{ ok: boolean; name: string }> {
    return request(`/work/projects/${id}/permanent`, { method: 'DELETE' })
  },

  listMembers(projectId: number): Promise<{ items: ProjectMember[] }> {
    return request(`/work/projects/${projectId}/members`)
  },

  getStatusReport(projectId: number): Promise<ProjectStatusReport> {
    return request(`/work/projects/${projectId}/status-report`)
  },

  listPhases(projectId: number): Promise<{ items: ProjectPhase[] }> {
    return request(`/work/projects/${projectId}/phases`)
  },

  createPhase(projectId: number, data: {
    name: string
    goal?: string | null
    start_date?: string | null
    end_date?: string | null
    team_id?: number | null
    status?: ProjectPhase['status']
  }): Promise<ProjectPhase> {
    return request(`/work/projects/${projectId}/phases`, { method: 'POST', body: JSON.stringify(data) })
  },

  updatePhase(projectId: number, phaseId: number, data: Partial<Pick<ProjectPhase, 'name' | 'goal' | 'start_date' | 'end_date' | 'team_id' | 'status' | 'order'>>): Promise<ProjectPhase> {
    return request(`/work/projects/${projectId}/phases/${phaseId}`, { method: 'PATCH', body: JSON.stringify(data) })
  },

  deletePhase(projectId: number, phaseId: number): Promise<void> {
    return request(`/work/projects/${projectId}/phases/${phaseId}`, { method: 'DELETE' })
  },

  listTaskDependencies(projectId: number): Promise<{ items: TaskDependency[] }> {
    return request(`/work/projects/${projectId}/task-dependencies`)
  },
}
